import React, { useEffect, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import { ColorScheme, colorSchemes, DEFAULT_PAGE_BACKGROUND, DEFAULT_CARD_BACKGROUND } from "../../constants/colorSchemes";
import {
  BACKGROUND_THEME_NONE,
  backgroundThemes,
  resolveBackgroundTheme,
} from "../../constants/backgroundThemes";
import { tourStepsSettings } from "./tourStepsSettings";
import GuidedTour from "../../components/GuidedTour";
import { getLuminance } from "../../utils/colorUtils";
import { applyColorSchemeColors, getDisplayBackgroundColor, isCardBackgroundChoiceDisabled, isDisplayTextChoiceDisabled } from "../../utils/themeColors";
import { useTour } from "../../context/TourContext";
import { adjustColorForColorblindness } from "../../utils/colorAdjustment";
import BubbleText from "../../components/BubbleText";
import { DEFAULT_FONT, formatFontForCss, normalizeFontName } from "../../utils/fonts";

/**
 * @fileoverview Application settings management component with theme and accessibility options.
 * 
 * @component Settings
 * @description Provides user interface for customizing application appearance and behavior,
 * including fonts, colors, and accessibility settings.
 * 
 * Features:
 * - Font customization (main, heading, button, display)
 * - Color theme selection
 * - Background theme options
 * - Accessibility settings
 *   - Colorblind mode
 *   - Colorblind type selection
 * - Theme-aware styling
 * - Guided tour integration
 * 
 * Font Options:
 * - Standard web fonts (Arial, Verdana, etc.)
 * - Custom educational fonts (Happy Series)
 * - Decorative fonts (Caveat, Chewy, etc.)
 * 
 * Theme Options:
 * - Color schemes with contrast checking
 * - Background image selection
 * - Text color customization
 * - Display text color settings
 */

const DEFAULT_THEME = {
  name: "White",
  backgroundColor: DEFAULT_PAGE_BACKGROUND,
  textColor: "#333333",
  displayTextColor: "#333333",
  originalDisplayTextColor: "#333333",
  displayBackgroundColor: DEFAULT_PAGE_BACKGROUND,
  cardBackgroundColor: DEFAULT_CARD_BACKGROUND,
  backgroundImage: BACKGROUND_THEME_NONE,
  isColorblindMode: false,
  colorblindType: "none",
  font: DEFAULT_FONT,
  headingFont: DEFAULT_FONT,
  buttonFont: DEFAULT_FONT,
  displayFont: DEFAULT_FONT,
  originalTextColor: "#333333",
  originalBackgroundColor: DEFAULT_PAGE_BACKGROUND,
};

const colorOptions = colorSchemes.map((scheme) => ({
  name: scheme.name,
  value: scheme.backgroundColor,
}));

const colorblindTypes = [
  "Protanopia",
  "Protanomaly",
  "Deuteranopia",
  "Deuteranomaly",
  "Tritanopia",
  "Tritanomaly",
  "Achromatopsia",
  "Anomalous Trichromacy",
];

const Settings: React.FC = () => {
  const {
    theme,
    setTheme,
    setDisplayTextColor,
    setCardBackgroundColor,
    setColorblindMode,
    setColorblindType,
    setFont,
    setHeadingFont,
    setButtonFont,
    setDisplayFont,
  } = useTheme();

  const { isGuidedTourEnabled, setIsGuidedTourEnabled } = useTour();

  const [isTourRunning, setIsTourRunning] = useState<boolean>(false);
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);
  const tourName = "settings";

  const steps = tourStepsSettings();

  const handleTourComplete = () => {
    console.log(`Tour ${tourName} completed`);
    localStorage.setItem(`tourCompleted_${tourName}`, "true");
    setIsTourRunning(false);
    setCurrentTourStep(0);
  };

  const handleStartTour = () => {
    setCurrentTourStep(0);
    setIsTourRunning(true);
  };

  useEffect(() => {
    const handleTourReset = () => {
      setCurrentTourStep(0);
      setIsTourRunning(false);
    };

    document.addEventListener("tourSkipped", handleTourReset);

    return () => {
      document.removeEventListener("tourSkipped", handleTourReset);
    };
  }, []);

  const handleTextColorChange = (color: string) => {
    console.log("Text color selected:", color);
    setDisplayTextColor(color);
  };

  const handleColorblindModeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = event.target.checked;
    setColorblindMode(isChecked);

    if (isChecked && !theme.colorblindType) {
      setColorblindType("protanopia");
    }
  };

  const handleColorblindTypeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setColorblindType(event.target.value);
    setTheme((prevTheme) => ({
      ...prevTheme,
      colorblindType: event.target.value,
    }));
  };

  const fonts = [
    "Happy Paragraphs Regular",
    "Comic Neue",
    "Arial",
    "Verdana",
    "Helvetica",
    "Times New Roman",
    "Courier New",
    "Caveat",
    "Patrick Hand",
    "Chewy",
    "Baloo 2",
    "KG What The Teacher Wants",
    "KG Shake It Off",
    "Happy Covers Regular",
    "Happy Covers Shadow Regular",
    "Happy Fun Letters Regular",
    "Happy Fun Titles Regular",
    "Happy Fun Titles Shadow Regular",
    "Happy Letters Regular",
    "Happy Medium Regular",
    "Happy Medium Shadow Regular",
    "Happy Neat Handwriting Regular",
    "Happy Task Card Labels Regular",
    "Happy Titles Regular",
  ];

  const handleFontChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedFont = normalizeFontName(e.target.value);
    setFont(selectedFont);
  };

  const handleHeadingFontChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setHeadingFont(normalizeFontName(e.target.value));
  };

  const handleButtonFontChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setButtonFont(normalizeFontName(e.target.value));
  };

  const handleDisplayFontChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDisplayFont(normalizeFontName(e.target.value));
  };

  const handleBackgroundThemeChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedValue = e.target.value;
    console.log("Background theme selected:", selectedValue);

    setTheme((prevTheme) => ({
      ...prevTheme,
      backgroundImage: resolveBackgroundTheme(selectedValue),
    }));
  };

  const handleColorThemeChange = (color: ColorScheme) => {
    setTheme((prevTheme) => {
      const nextCardBackground =
        prevTheme.cardBackgroundColor &&
        isCardBackgroundChoiceDisabled(
          prevTheme.cardBackgroundColor,
          color.backgroundColor,
        )
          ? DEFAULT_CARD_BACKGROUND
          : prevTheme.cardBackgroundColor || DEFAULT_CARD_BACKGROUND;

      return {
        ...applyColorSchemeColors(color),
        isColorblindMode: prevTheme.isColorblindMode,
        colorblindType: prevTheme.colorblindType,
        font: prevTheme.font,
        headingFont: prevTheme.headingFont,
        buttonFont: prevTheme.buttonFont,
        displayFont: prevTheme.displayFont,
        adjustColorForColorblindness: prevTheme.adjustColorForColorblindness,
        cardBackgroundColor: nextCardBackground,
        backgroundImage: resolveBackgroundTheme(prevTheme.backgroundImage),
      };
    });
  };

  // Load the guided tour preference from localStorage
  useEffect(() => {
    const storedPreference = localStorage.getItem("guidedTourEnabled");
    if (storedPreference !== null) {
      const parsedPreference = JSON.parse(storedPreference);
      setIsGuidedTourEnabled((prev) => {
        if (prev !== parsedPreference) {
          return parsedPreference;
        }
        return prev;
      });
    }
  }, [setIsGuidedTourEnabled]);

  useEffect(() => {
    console.log("Tour state:", {
      isTourRunning,
      currentTourStep,
      steps,
    });
  }, [isTourRunning, currentTourStep, steps]);

  const displayBackground = getDisplayBackgroundColor(theme);
  const pageBackground = theme.originalBackgroundColor || theme.backgroundColor;

  const isDisplayTextDisabled = (color: string) =>
    isDisplayTextChoiceDisabled(color, displayBackground);

  const isCardBackgroundDisabled = (color: string) =>
    isCardBackgroundChoiceDisabled(color, pageBackground);

  // Function to get adjusted color options based on colorblind type
  const getAdjustedColorOptions = () => {
    return colorOptions.map((color) => ({
      ...color,
      adjustedValue: theme.isColorblindMode
        ? adjustColorForColorblindness(color.value, theme.colorblindType)
        : color.value,
    }));
  };

  // Function to get adjusted text color options based on colorblind type
  const getAdjustedTextColorOptions = () => {
    return colorOptions.map((color) => ({
      ...color,
      adjustedValue: theme.isColorblindMode
        ? adjustColorForColorblindness(color.value, theme.colorblindType)
        : color.value,
    }));
  };

  const handleResetToDefaults = () => {
    setTheme((prevTheme) => ({
      ...DEFAULT_THEME,
      adjustColorForColorblindness: prevTheme.adjustColorForColorblindness,
      font: DEFAULT_FONT,
      headingFont: DEFAULT_FONT,
      buttonFont: DEFAULT_FONT,
      displayFont: DEFAULT_FONT,
    }));

    setFont(DEFAULT_FONT);
    setHeadingFont(DEFAULT_FONT);
    setButtonFont(DEFAULT_FONT);
    setDisplayFont(DEFAULT_FONT);

    setDisplayTextColor(DEFAULT_THEME.displayTextColor);

    const cssFont = formatFontForCss(DEFAULT_FONT);
    document.documentElement.style.setProperty("--font-family", cssFont);
    document.documentElement.style.setProperty("--heading-font-family", cssFont);
    document.documentElement.style.setProperty("--button-font-family", cssFont);
    document.documentElement.style.setProperty("--display-font-family", cssFont);
    document.documentElement.style.setProperty("--background-image", "none");
  };

  return (
    <div
      className="page-container text-theme-text flex flex-col items-center"
    >
      <div className="mb-4 flex w-full justify-end">
        <label className="guided-tour-toggle flex items-center">
          <input
            type="checkbox"
            checked={isGuidedTourEnabled}
            onChange={(e) => setIsGuidedTourEnabled(e.target.checked)}
            className="mr-2"
          />
          Enable Guided Tour
        </label>
      </div>

      <h1 className={`settings inherit mb-8 text-3xl font-bold`}>
        <BubbleText>Settings</BubbleText>
      </h1>
      <div className="w-full space-y-6 px-4 md:px-8">
        <div>
          <h2 className="inherit mb-2 text-xl font-semibold">Main Font</h2>
          <select
            value={normalizeFontName(theme.font)}
            onChange={handleFontChange}
            className="main-font min-h-[44px] w-full rounded border border-black bg-white p-2 text-black"
            title="Select main font"
          >
            {fonts.map((font) => {
              const fontName = font.replace(/^["'](.+)["']$/, "$1");
              return (
                <option
                  key={font}
                  value={font}
                  style={{ fontFamily: fontName }}
                >
                  {fontName}
                </option>
              );
            })}
          </select>
        </div>

        <div>
          <h2 className="inherit mb-2 text-xl font-semibold">Heading Font</h2>
          <select
            value={normalizeFontName(theme.headingFont)}
            onChange={handleHeadingFontChange}
            className="heading-font min-h-[44px] w-full rounded border border-black bg-white p-2 text-black"
            title="Select heading font"
          >
            {fonts.map((font) => {
              const fontName = font.replace(/^["'](.+)["']$/, "$1");
              return (
                <option
                  key={font}
                  value={font}
                  style={{ fontFamily: fontName }}
                >
                  {fontName}
                </option>
              );
            })}
          </select>
        </div>

        <div>
          <h2 className="inherit mb-2 text-xl font-semibold">Button Font</h2>
          <select
            value={normalizeFontName(theme.buttonFont)}
            onChange={handleButtonFontChange}
            className="button-font min-h-[44px] w-full rounded border border-black bg-white p-2 text-black"
            title="Select button font"
          >
            {fonts.map((font) => {
              const fontName = font.replace(/^["'](.+)["']$/, "$1");
              return (
                <option
                  key={font}
                  value={font}
                  style={{ fontFamily: fontName }}
                >
                  {fontName}
                </option>
              );
            })}
          </select>
        </div>

        <div>
          <h2 className="inherit mb-2 text-xl font-semibold">
            Full Screen Display Font
          </h2>
          <select
            value={normalizeFontName(theme.displayFont)}
            onChange={handleDisplayFontChange}
            className="display-font min-h-[44px] w-full rounded border border-black bg-white p-2 text-black"
            title="Select full screen display font"
          >
            {fonts.map((font) => {
              const fontName = font.replace(/^["'](.+)["']$/, "$1");
              return (
                <option
                  key={font}
                  value={font}
                  style={{ fontFamily: fontName }}
                >
                  {fontName}
                </option>
              );
            })}
          </select>
        </div>

        <div className="mb-4 w-full text-left">
          <button
            type="button"
            onClick={handleResetToDefaults}
            className="rounded-md bg-red-500 px-4 py-2 text-white transition-colors hover:bg-red-600"
            title="Reset all settings to default values"
          >
            Reset to Default Settings
          </button>
        </div>

        <div className="mb-4">
          <label className="inherit mb-2 block font-bold">Color Theme:</label>
          <p className="inherit mb-2 text-sm opacity-80">
            Page text is chosen automatically so it stays readable on this
            color.
          </p>
          <div className="flex flex-wrap">
            {getAdjustedColorOptions().map((color) => (
              <div
                key={color.name}
                className={`color-theme m-1 inline-block size-10 cursor-pointer border border-black transition-all duration-300 sm:size-8 ${theme.name === color.name ? "border-4 border-black" : ""}`}
                style={{
                  backgroundColor: color.adjustedValue,
                  ...(theme.name === color.name &&
                  color.adjustedValue.toLowerCase() === "#000000"
                    ? { backgroundColor: "#404040" }
                    : {}),
                }}
                title={color.name}
                onClick={() => {
                  const newTheme = colorSchemes.find(
                    (scheme) => scheme.name === color.name,
                  );
                  if (newTheme) {
                    handleColorThemeChange(newTheme);
                  }
                }}
              />
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="inherit mb-2 block font-bold">
            Text Color for Full Screen Display:
          </label>
          <p className="inherit mb-2 text-sm opacity-80">
            Colors that would blend into the full-screen background are
            unavailable.
          </p>
          <div className="flex flex-wrap">
            {getAdjustedTextColorOptions().map((color) => {
              const selected =
                (theme.originalDisplayTextColor || theme.displayTextColor) ===
                color.value;
              const disabled = isDisplayTextDisabled(color.value);
              return (
                <div
                  key={color.name}
                  className={`text-color m-1 inline-block size-10 cursor-pointer border border-black transition-all duration-300 sm:size-8 ${selected ? "border-4 border-black" : ""} ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                  style={{ backgroundColor: color.adjustedValue }}
                  title={
                    disabled
                      ? `${color.name} — low contrast on the current background`
                      : color.name
                  }
                  onClick={() => {
                    if (!disabled) {
                      handleTextColorChange(color.value);
                    }
                  }}
                />
              );
            })}
          </div>
        </div>

        <div className="mb-4">
          <label className="card-background-color inherit mb-2 block font-bold">
            Collection Card Background:
          </label>
          <p className="inherit mb-2 text-sm opacity-80">
            This is the area around each collection card, and the background of
            Edit, Preview, and Start pop-ups. It does not change the
            alternating colors inside the cards. The same color as the page
            theme is unavailable so cards stay visible.
          </p>
          <div className="flex flex-wrap">
            {[
              {
                name: "White",
                value: DEFAULT_CARD_BACKGROUND,
                adjustedValue: DEFAULT_CARD_BACKGROUND,
              },
              ...getAdjustedColorOptions(),
            ].map((color) => {
              const selected =
                (theme.cardBackgroundColor || DEFAULT_CARD_BACKGROUND)
                  .toLowerCase() === color.value.toLowerCase();
              const disabled = isCardBackgroundDisabled(color.value);
              return (
                <div
                  key={`card-${color.name}-${color.value}`}
                  className={`m-1 inline-block size-10 border border-black transition-all duration-300 sm:size-8 ${selected ? "border-4 border-black" : ""} ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                  style={{
                    backgroundColor: color.adjustedValue || color.value,
                  }}
                  onClick={() => {
                    if (!disabled) {
                      setCardBackgroundColor(color.value);
                    }
                  }}
                  title={
                    disabled
                      ? `${color.name} — too close to the page color`
                      : color.name
                  }
                />
              );
            })}
          </div>
        </div>

        <div>
          <h2 className="inherit mb-2 text-xl font-semibold">Accessibility</h2>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={theme.isColorblindMode}
                onChange={handleColorblindModeChange}
                className="accessibility mr-2"
              />
              Enable Colorblind Mode
            </label>
            {theme.isColorblindMode && (
              <select
                value={theme.colorblindType}
                onChange={handleColorblindTypeChange}
                className="min-h-[44px] w-full max-w-none rounded border p-2 text-black sm:max-w-xs"
                title="Select colorblind type"
              >
                {colorblindTypes.map((type) => (
                  <option key={type} value={type.toLowerCase()}>
                    {type}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div>
          <h2 className="inherit mb-2 text-xl font-semibold">
            Background Theme
          </h2>
          <p className="inherit mb-2 text-sm opacity-80">
            Cartoon silhouettes that sit on top of your background color. They
            stay put if you change the color.
          </p>
          <select
            value={resolveBackgroundTheme(theme.backgroundImage)}
            onChange={handleBackgroundThemeChange}
            className="background-theme min-h-[44px] w-full rounded border border-black bg-white p-2 text-black"
            title="Select background theme"
          >
            {backgroundThemes.map((backgroundTheme) => (
              <option
                key={backgroundTheme.name}
                value={backgroundTheme.value}
              >
                {backgroundTheme.name}
              </option>
            ))}
          </select>
          {resolveBackgroundTheme(theme.backgroundImage) !==
            BACKGROUND_THEME_NONE && (
            <div
              className="relative mt-3 h-24 w-full max-w-md overflow-hidden rounded-lg border-2 border-black"
              style={{
                backgroundColor:
                  theme.displayBackgroundColor || theme.backgroundColor,
              }}
              aria-hidden="true"
            >
              <div
                className="absolute inset-0"
                style={{
                  opacity: 0.2,
                  backgroundColor:
                    getLuminance(
                      theme.displayBackgroundColor ||
                        theme.backgroundColor ||
                        "#F8F9FA",
                    ) < 0.45
                      ? "#ffffff"
                      : "#1a1a1a",
                  WebkitMaskImage: `url(${resolveBackgroundTheme(theme.backgroundImage)})`,
                  maskImage: `url(${resolveBackgroundTheme(theme.backgroundImage)})`,
                  WebkitMaskRepeat: "repeat",
                  maskRepeat: "repeat",
                  WebkitMaskSize: "140px 140px",
                  maskSize: "140px 140px",
                }}
              />
            </div>
          )}
        </div>
      </div>

      <GuidedTour
        steps={steps}
        isRunning={isTourRunning && isGuidedTourEnabled}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={setCurrentTourStep}
        tourName={tourName}
        onStart={handleStartTour}
      />
    </div>
  );
};

export default Settings;
